/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { TextValueTranslation } from '@msdyn365-commerce/retail-proxy';
import { IActionContext, IAny, IDictionary, IRequestContext } from '../interfaces';
import { ICategoryPath, ICategoryUrl } from '../interfaces/ICategoryPathInterfaces';
import { IParameterizeFeatureFlags, parameterize } from './parameterize';
import { versionGte } from './version-utils';

export const enum PageType {
    Product = 'product',
    Category = 'category',
    List = 'list',
    Cart = 'cart',
    Checkout = 'checkout',
    Account = 'account',
    OrderHistory = 'order-history',
    OrderLookup = 'order-lookup',
    Wishlist = 'wishlist',
    GiftcardBalance = 'giftcard-balance',
    FindStores = 'find-stores'
}
export const rcsuVersionCacheKey = 'MSDyn365Commerce_RCSUVERSION_Key';
export const rcsuVersionTypeName = 'MSDyn365Commerce_RCSUVERSION';

// Query params that must be appened to any URL generated when in preview mode
const PASSTHROUGH_QUERY_PARAMS = ['locale', 'market', 'sitepath', 'hostoverride', 'preview', 'domain', 'catalogid'];

type segment = { Name: string; RecordId: string | number };
type categoryContext = { categories?: { Name: string; RecordId: string | number; NeutralizedName?: string }[] };
type categorySlugContext = { category?: { Slug: string; RecordId: string | number } };
type productContext = { product?: { Name: string; RecordId: string | number; NeutralizedName?: string } };
type listContext = { list?: { Name: string; NeutralizedName?: string } };

type pageTypeContext = categoryContext & productContext & listContext & productContext & { [property: string]: IAny };
type routeBuilder = (context?: pageTypeContext, ctx?: IActionContext) => string;
type IRouteDiconary = IDictionary<routeBuilder>;

const categorySegment = (context?: categoryContext & categorySlugContext, ctx?: IActionContext) => {
    if (!context) {
        return '';
    }
    if (context.categories) {
        const parameterizeFeatureFlags: IParameterizeFeatureFlags = getParameterizeFeatureFlagsWithRequestContext(
            ctx && ctx.requestContext
        );
        return context.categories
            .map(record => `/${parameterize(record.Name, undefined, parameterizeFeatureFlags, record.NeutralizedName)}`)
            .join('');
    } else if (context.category) {
        return context.category.Slug;
    }
    return '';
};
const categoryMoniker = (context?: categoryContext & categorySlugContext) => {
    if (context) {
        if (context.categories) {
            return `/${context.categories[context.categories.length - 1].RecordId}.c`;
        }
        if (context.category) {
            return `/${context.category.RecordId}.c`;
        }
    }
    return '';
};

const productMoniker = (context?: productContext, ctx?: IActionContext) => {
    if (context) {
        if (context.product && context.product.Name !== '') {
            const parameterizeFeatureFlags: IParameterizeFeatureFlags = getParameterizeFeatureFlagsWithRequestContext(
                ctx && ctx.requestContext
            );
            return `/${parameterize(context.product.Name, undefined, parameterizeFeatureFlags, context.product.NeutralizedName)}/${
                context.product.RecordId
            }.p`;
        } else if (context.product) {
            return `/${context.product.RecordId}.p`;
        }
    }
    return '';
};

const listMoniker = (context?: listContext, ctx?: IActionContext) => {
    const parameterizeFeatureFlags: IParameterizeFeatureFlags = getParameterizeFeatureFlagsWithRequestContext(ctx && ctx.requestContext);
    return context && context.list
        ? `/${parameterize(context.list.Name, undefined, parameterizeFeatureFlags, context.list.NeutralizedName)}.l`
        : '';
};

const platformRoutes: IRouteDiconary = {
    category: (context, ctx?) => `${categorySegment(context, ctx)}${categoryMoniker(context)}`,
    product: (context, ctx?) => `${categorySegment(context, ctx)}${productMoniker(context, ctx)}`,
    list: listMoniker
};

/**
 * Forces strings to have a leading slash and no trailing slash
 */
const ensureSlashConsistency = (name: string) => {
    if (name[0] !== '/') {
        name = `/${name}`;
    }
    if (name[name.length - 1] === '/') {
        name = name.slice(0, -1);
    }
    return name;
};

/**
 * Correctly appends site path if required in the context
 * @param route Current route
 * @param ctx Action Context
 */
const populateSitePath = (route: string[], ctx: IActionContext): string[] => {
    const sitePath = ctx && ctx.requestContext && ctx.requestContext.sitePath;
    sitePath && route.unshift(sitePath);
    return route;
};

const overrideDomain = (domain: string, hostName?: string): string | undefined => {
    if (!hostName) {
        return domain;
    }
    if (domain !== hostName) {
        return domain;
    } else {
        return undefined;
    }
};

/**
 * Appends any query params that should be appended if preview mode is set to true
 * and returns the final result
 *
 * @param route The partially constructed destination url
 * @param queryParams The list of query params in the initial request
 */
export const appendQueryParams = (
    route: string | undefined,
    queryParams?: IDictionary<string>,
    hostName?: string,
    skipCatalogId?: boolean
): string => {
    if (route && queryParams && (queryParams.preview || queryParams.domain || queryParams.catalogid)) {
        const query = Object.keys(queryParams)
            .map(key => {
                if (PASSTHROUGH_QUERY_PARAMS.indexOf(key) !== -1) {
                    if (key.toLowerCase() === 'catalogid' && skipCatalogId) {
                        return '';
                    }

                    if (key === 'domain') {
                        // only add domain parameter if domain is different with hostName
                        const newDomain = overrideDomain(queryParams[key], hostName);
                        return !!newDomain ? `${key}=${newDomain}` : '';
                    } else {
                        return `${key}=${queryParams[key]}`;
                    }
                } else {
                    return '';
                }
            })
            .filter(Boolean)
            .join('&');

        return query.length ? `${route}${route.indexOf('?') === -1 ? '?' : '&'}${query}` : route;
    }

    return route || '';
};

const appendQueryString = (route: string | undefined, context: IActionContext, catalogId?: string) => {
    const query = { ...(context && context.requestContext && context.requestContext.query) } || {};
    // override the catalogid in query when we passed the catalogId
    if (context.requestContext?.user?.isB2b && catalogId) {
        query['catalogid'] = catalogId;
    }
    if (
        context.requestContext?.user?.isB2b &&
        ((query['catalogid'] && query['catalogid'] !== '0') ||
            (context.requestContext?.user?.catalogId && context.requestContext.user.catalogId !== '0'))
    ) {
        query['catalogid'] = query['catalogid'] || context.requestContext?.user?.catalogId || '';
    } else {
        delete query['catalogid'];
    }
    return appendQueryParams(route, query, context?.requestContext?.url?.requestUrl?.hostname);
};

function getRoutePath(
    route: string | routeBuilder | undefined,
    ctx: IActionContext,
    routeContext: pageTypeContext,
    catalogId?: string
): string {
    let returnUrl = '';
    if (route) {
        if (typeof route === 'function') {
            route = route(routeContext, ctx);
        }
        returnUrl = populateSitePath([route], ctx)
            .map(ensureSlashConsistency)
            .join('');
    }

    return appendQueryString(returnUrl, ctx, catalogId);
}

/**
 * Gets the corresponding route for the given page type
 * @param pageType the type of page that needs to be routed to.
 * @param ctx current action context.
 */
export const getUrlSync = (
    pageType: PageType | string,
    ctx: IActionContext,
    routeContext: pageTypeContext = {},
    catalogid?: string
): string | undefined => {
    let route: string | routeBuilder | undefined;
    if (platformRoutes[pageType]) {
        return getRoutePath(platformRoutes[pageType], ctx, routeContext, catalogid);
    }

    const requestContext: IRequestContext = ctx && ctx.requestContext;
    if (requestContext) {
        route = requestContext.query && requestContext.query[pageType];
        if (route) {
            return getRoutePath(route, ctx, routeContext, catalogid);
        }

        const appRoutes = requestContext.app && requestContext.app.routes;
        if (appRoutes) {
            const pageRouteFromAppSettings = appRoutes[pageType];
            if (pageRouteFromAppSettings && typeof pageRouteFromAppSettings === 'object') {
                return appendQueryString(pageRouteFromAppSettings.destinationUrl, ctx, catalogid);
            } else if (pageRouteFromAppSettings && typeof pageRouteFromAppSettings === 'string') {
                return getRoutePath(pageRouteFromAppSettings, ctx, routeContext, catalogid);
            }
        }
    }

    return getRoutePath(pageType, ctx, routeContext, catalogid);
};

export const isMinRcsuVersionForCatalogId = (rcsuVersion: string | undefined) => {
    if (rcsuVersion && typeof rcsuVersion === 'string' && versionGte(rcsuVersion, '9.35.22007.2')) {
        return true;
    }
    return false;
};

// Used by SSK code
export const getCatalogId = (requestContext: IRequestContext): number => {
    let catalogid: number = 0;
    if (requestContext?.user?.isB2b) {
        const catalogidstr = requestContext.query?.catalogid || requestContext.user?.catalogId || '0';
        catalogid = Number(catalogidstr) || 0;
    }
    return catalogid;
};

// Used by SDK, to maintain backwards compatibility with Retail proxy
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getCatalogIdForSdk = (requestContext: IRequestContext, fallbackCatalogId: any): any => {
    let catalogid = 0;
    if (requestContext?.user?.isB2b) {
        const isCompatibleRcsuVersionForCatalogId = isMinRcsuVersionForCatalogId(requestContext?.rcsuVersion);
        if (isCompatibleRcsuVersionForCatalogId) {
            catalogid = getCatalogId(requestContext);
        } else {
            catalogid = fallbackCatalogId;
        }
    }
    return catalogid;
};

export const getCategoriesUrlSync = (categoriesPath: ICategoryPath, ctx: IActionContext): ICategoryUrl[] | undefined => {
    const categoriesPathUrls: ICategoryUrl[] = [];
    const tempSegments: segment[] = [];
    if (categoriesPath && categoriesPath.Segments) {
        categoriesPath.Segments.map(seg => {
            tempSegments.push({
                Name: seg.CategoryName || '',
                RecordId: seg.CategoryId || ''
            });
            const context = {
                categories: tempSegments
            };
            categoriesPathUrls.push({
                Name: seg.CategoryName,
                Url: getUrlSync(PageType.Category, ctx, context) || ''
            });
        });
    }

    return categoriesPathUrls;
};

/**
 * Gets localized friendlyName from category NameTranslations object based on the locale
 * @param locale Locale from current requestContext
 * @param nameTranslations LocalizedNames for a given category.
 */
export const getFriendlyName = (locale?: string, nameTranslations?: TextValueTranslation[]): string | undefined => {
    let nameTranslation: TextValueTranslation | undefined;
    if (locale && nameTranslations && nameTranslations.length > 0) {
        nameTranslation = nameTranslations.find(item => item.Language!.toLowerCase() === locale.toLowerCase());
    }

    return nameTranslation && nameTranslation.Text;
};

/**
 * Return ParameterizedFeatureFlags based on requestContext
 * @param ctx Action Context
 */
export const getParameterizeFeatureFlags = (ctx?: IActionContext): IParameterizeFeatureFlags => {
    return {
        enableUrlEncoding: (ctx && ctx.requestContext.features && ctx.requestContext.features.enableUrlEncoding) || false,
        enableUrlLocalization: (ctx && ctx.requestContext.features && ctx.requestContext.features.enableUrlLocalization) || false
    };
};

/**
 * Return ParameterizedFeatureFlags based on requestContext
 * @param ctx Request Context
 */
export const getParameterizeFeatureFlagsWithRequestContext = (ctx?: IRequestContext): IParameterizeFeatureFlags => {
    return {
        enableUrlEncoding: (ctx && ctx.features && ctx.features.enableUrlEncoding) || false,
        enableUrlLocalization: (ctx && ctx.features && ctx.features.enableUrlLocalization) || false
    };
};
